import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Spinner,
  Image,
  Grid,
  GridItem,
  Link,
  Flex,
  Button,
  Center,
  Progress,
  Divider,
} from "@chakra-ui/react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureFetch } from "../data/secureFetch";
import { buildBannerPlaceholderUrl } from "../utils/bannerImage";

const OrgProfile = () => {
  const { org_id } = useParams();
  const [org, setOrg] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrgAndCampaigns = async () => {
      try {
        const [orgRes, campaignsRes] = await Promise.all([
          secureFetch(`/api/organizations/${org_id}`),
          secureFetch(`/api/campaigns/organization/${org_id}`),
        ]);

        if (!orgRes.ok || !campaignsRes.ok)
          throw new Error("Failed to fetch organization or campaigns");

        setOrg(orgRes);
        // /api/campaigns/organization/:id can return either a paginated { data: [...], pagination }
        // shape or a plain array — normalise to always work with the array.
        const rawCampaigns = campaignsRes;
        const campaignList = Array.isArray(rawCampaigns)
          ? rawCampaigns
          : Array.isArray(rawCampaigns?.data)
            ? rawCampaigns.data
            : [];
        setCampaigns(campaignList);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgAndCampaigns();
  }, [org_id]);

  // Normalize external website URL to always include protocol
  const getExternalUrl = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    const url = raw.trim();
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url; // already absolute
    if (/^\/\//.test(url)) return `https:${url}`; // protocol-relative
    return `https://${url}`; // default to https
  };

  // Display-friendly version for anchor text
  const getDisplayUrl = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    return raw.trim().replace(/^https?:\/\//i, "");
  };

  const toSnakeSlug = (value = "") =>
    (value || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "org";

  if (loading)
    return (
      <Center mt={10}>
        <Spinner size="xl" />
      </Center>
    );
  if (error)
    return (
      <Box p={6}>
        <Text color="red.500">{error}</Text>
      </Box>
    );
  if (!org) return null;
  const orgDonationSlug = org.donation_url || toSnakeSlug(org.name);
  const shareTitle = `${org.name} | PurelyGive`;
  const shareDescription =
    org.description ||
    `Support ${org.name} by donating to their active campaigns on PurelyGive.`;
  const firstCampaignWithBanner = campaigns.find(
    (campaign) => campaign.banner_image_url,
  );
  const shareImage =
    firstCampaignWithBanner?.banner_image_url ||
    buildBannerPlaceholderUrl(org.name || "Organization");
  const shareUrl = window.location.href;

  return (
    <Box p={6} maxW="7xl" mx="auto">
      <Helmet>
        <title>{shareTitle}</title>
        <meta name="description" content={shareDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="PurelyGive" />
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={shareTitle} />
        <meta name="twitter:description" content={shareDescription} />
        <meta name="twitter:image" content={shareImage} />
      </Helmet>

      <Box
        mb={10}
        textAlign="center"
        bg="gray.50"
        p={6}
        borderRadius="xl"
        boxShadow="sm"
      >
        <Heading size="lg" mb={2}>
          {org.name}
        </Heading>
        {org.description && (
          <Text fontSize="md" color="gray.700">
            {org.description}
          </Text>
        )}
        <Button
          as={RouterLink}
          to={`/donate/${orgDonationSlug}`}
          colorScheme="orange"
          mt={4}
          size="md"
        >
          Donate to {org.name}
        </Button>
        <Flex mt={4} justify="center" wrap="wrap" gap={4} color="gray.600">
          {org.website_url && (
            <Link
              href={getExternalUrl(org.website_url)}
              isExternal
              color="blue.500"
            >
              {getDisplayUrl(org.website_url)}
            </Link>
          )}
          {org.city && (
            <Text>
              {org.city}, {org.state} {org.zip_code}
            </Text>
          )}
          {org.email && <Text>Email: {org.email}</Text>}
        </Flex>
      </Box>

      <Heading size="lg" mb={4}>
        Current Campaigns
      </Heading>
      {campaigns.length === 0 ? (
        <Text>No active campaigns for this organization.</Text>
      ) : (
        <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
          {campaigns.map((c) => {
            const percent =
              c.goal > 0 ? Math.min((c.amount_raised / c.goal) * 100, 100) : 0;
            return (
              <GridItem key={c.id}>
                <Box
                  borderWidth="1px"
                  borderRadius="lg"
                  overflow="hidden"
                  p={4}
                  boxShadow="sm"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  minH="420px"
                >
                  <Image
                    src={
                      c.banner_image_url ||
                      buildBannerPlaceholderUrl(c.name || "Campaign")
                    }
                    alt={c.name}
                    mb={3}
                    borderRadius="md"
                    objectFit="cover"
                    height="200px"
                    width="100%"
                  />
                  <Heading size="md" mb={2} noOfLines={2}>
                    {c.name}
                  </Heading>
                  {c.goal > 0 && (
                    <Box mb={3}>
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        ${(c.amount_raised / 100).toFixed(2)} raised of $
                        {(c.goal / 100).toFixed(2)}
                      </Text>
                      <Progress
                        value={percent}
                        size="sm"
                        colorScheme="orange"
                        borderRadius="md"
                      />
                    </Box>
                  )}
                  {c.description && (
                    <Text mb={2} fontSize="sm" color="gray.700" noOfLines={3}>
                      {c.description}
                    </Text>
                  )}
                  <Flex mt="auto">
                    <Button
                      as={RouterLink}
                      to={`/donate/${c.url}`}
                      colorScheme="orange"
                      size="sm"
                      mx="auto"
                    >
                      Donate
                    </Button>
                  </Flex>
                </Box>
              </GridItem>
            );
          })}
        </Grid>
      )}

      <Divider my={10} />
      <Flex
        direction="column"
        align="center"
        fontSize="sm"
        color="gray.500"
        mt={10}
        px={4}
        py={6}
        borderTop="1px solid"
        borderColor="gray.200"
        textAlign="center"
      >
        <Flex align="center" mb={2}>
          <Image
            src="../../logo192.png"
            alt="PurelyGive Logo"
            boxSize="20px"
            mr={2}
          />
          <Text>© 2026 PurelyGive. All rights reserved.</Text>
        </Flex>
        <Flex gap={4} wrap="wrap" justify="center">
          <Link as={RouterLink} to="/">
            Home
          </Link>
          <Link as={RouterLink} to="/privacy-policy">
            Privacy Policy
          </Link>
          <Link as={RouterLink} to="/terms">
            Terms & Conditions
          </Link>
          <Link as={RouterLink} to="/contact">
            Contact Us
          </Link>
        </Flex>
      </Flex>
    </Box>
  );
};

export default OrgProfile;
